import type { ReactElement } from 'react';
import {
  FEATURE_PLANNER_DASHBOARD,
  FEATURE_TEAM_LEADER_DASHBOARD,
} from '../ftlDashboards/ftlDashboardsApi';

/**
 * Filtry banku, do których prowadzą pulpity FTL (makieta back office v2,
 * fala A, A4; przełączniki `plannerDashboard` i `teamLeaderDashboard`,
 * 20.10.2026). Klucze i wartości to kontrakt z API
 * (`user-offers/ftl-dashboard-filters.ts`) i dane, które react-admin
 * zapamiętuje użytkownikom — nie przemianowujemy. Pola stoją w „Dodaj
 * filtr”, każde za swoim przełącznikiem; kafelek pulpitu otwiera listę
 * z wartością, więc pole pokazuje się samo.
 */

export { FEATURE_PLANNER_DASHBOARD, FEATURE_TEAM_LEADER_DASHBOARD };
/** Alert przed załadunkiem — bez niego „jutro załadunek” byłby zawsze pusty. */
export const FEATURE_LOADING_TOMORROW_ALERT = 'loadingTomorrowAlert';

/** „Moje warianty” — wartości klucza `myVariants`. */
export const MY_VARIANTS_CHOICES = [
  { id: 'active', name: 'czekają na decyzję' },
  { id: 'expiringToday', name: 'termin mija dziś' },
  { id: 'expired', name: 'po terminie' },
  { id: 'conflict', name: 'w konflikcie w czasie' },
];

/** „Wniosek o odpięcie” — wartości klucza `unassignRequest`. */
export const UNASSIGN_REQUEST_CHOICES = [
  { id: 'pending', name: 'czeka na decyzję' },
  { id: 'mine', name: 'mój — czeka na decyzję' },
];

/**
 * Wnioski po wyjeździe auta (makieta back office v2, B6, 21.10.2026) —
 * wartość `changePending`, do której prowadzi kafelek Team Leadera; tylko
 * z którymkolwiek z przełączników `vehicleChangeRequest`,
 * `tradeTransferBeforeDeparture`.
 */
export const CHANGE_REQUEST_CHOICE = {
  id: 'changePending',
  name: 'o zmianę pojazdu albo przekazanie — czeka na decyzję',
};

/** Wartości `unassignRequest` z wnioskami po wyjeździe, gdy działa któryś z przełączników B6. */
export function unassignRequestChoices<C extends { id: string; name: string }>(
  choices: readonly C[],
  on: boolean,
): Array<C | typeof CHANGE_REQUEST_CHOICE> {
  if (!on) return [...choices];
  const [pending, ...rest] = choices;
  return pending ? [pending, CHANGE_REQUEST_CHOICE, ...rest] : [CHANGE_REQUEST_CHOICE];
}

/** „Z wariantem” — nowa wartość klucza „Znaczniki” (`forklift`), para do „Bez wariantu”. */
export const MARKERS_FILTER_WITH_VARIANT = 'withVariant';
export const WITH_VARIANT_CHOICE = { id: MARKERS_FILTER_WITH_VARIANT, name: 'Z wariantem' };

/** Opcje „Znaczników” z „Z wariantem”, gdy działa pulpit Team Leadera. */
export function withWithVariantChoice<C extends { id: string; name: string }>(
  choices: readonly C[],
  on: boolean,
): Array<C | typeof WITH_VARIANT_CHOICE> {
  return on ? [...choices, WITH_VARIANT_CHOICE] : [...choices];
}

/**
 * Klucz filtra → czy działa dla konta (jak `FTL_DASHBOARD_FILTER_FEATURES`
 * w API: wnioski — z którymkolwiek pulpitem; „jutro załadunek” — z pulpitem
 * Team Leadera i alertem przed załadunkiem).
 */
export const FTL_DASHBOARD_FILTER_RULES: Record<string, (on: (f: string) => boolean) => boolean> = {
  myVariants: (on) => on(FEATURE_PLANNER_DASHBOARD),
  myRegion: (on) => on(FEATURE_PLANNER_DASHBOARD),
  unassignRequest: (on) => on(FEATURE_PLANNER_DASHBOARD) || on(FEATURE_TEAM_LEADER_DASHBOARD),
  handedToTradeToday: (on) => on(FEATURE_TEAM_LEADER_DASHBOARD),
  loadingAlert: (on) => on(FEATURE_TEAM_LEADER_DASHBOARD) && on(FEATURE_LOADING_TOMORROW_ALERT),
};

/**
 * Dokłada filtry pulpitów, których przełącznik działa. Klucz, który lista
 * już ma (np. ten sam filtr z innej funkcji), nie wchodzi drugi raz.
 */
export function withFtlDashboardFilters(
  filters: ReactElement[],
  extra: ReactElement[],
  on: (feature: string) => boolean,
): ReactElement[] {
  const sourceOf = (f: ReactElement) => (f.props as { source?: string }).source ?? '';
  const present = new Set(filters.map(sourceOf));
  const shown = extra.filter((f) => {
    const rule = FTL_DASHBOARD_FILTER_RULES[sourceOf(f)];
    return rule !== undefined && rule(on) && !present.has(sourceOf(f));
  });
  return shown.length > 0 ? [...filters, ...shown] : filters;
}
